import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageSquare, MessageCircle, CheckCheck } from "lucide-react";
import api, { timeAgo } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 45000;
const MAX_NOTIFS = 40;

const baseKey = (uid) => `compass_notif_base_${uid}`;
const dmKey = (uid) => `compass_notif_dm_${uid}`;
const listKey = (uid) => `compass_notif_list_${uid}`;

const readJSON = (k, fallback) => {
  try {
    const v = JSON.parse(localStorage.getItem(k));
    return v ?? fallback;
  } catch { return fallback; }
};
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

const totalReactions = (reactions) =>
  Object.values(reactions || {}).reduce((s, n) => s + (Number(n) || 0), 0);

const snippet = (msg) => {
  const t = (msg || "").replace(/\s+/g, " ").trim();
  return t.length > 48 ? t.slice(0, 48) + "…" : t || "your post";
};

const NotificationBell = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const uid = user?.id;
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);
  const busy = useRef(false);

  useEffect(() => {
    if (!uid) { setNotifs([]); return; }
    setNotifs(readJSON(listKey(uid), []));
  }, [uid]);

  const poll = useCallback(async () => {
    if (!uid || busy.current) return;
    busy.current = true;
    const newItems = [];
    try {
      // ---- Reactions & comments on my posts ----
      try {
        const { data } = await api.get("/community-v2/feed/combined");
        const mine = (data.feed || []).filter((p) => p.is_mine);
        const base = readJSON(baseKey(uid), null);
        const firstRun = base === null;
        const nextBase = {};
        for (const p of mine) {
          const r = totalReactions(p.reactions);
          const c = Number(p.comment_count) || 0;
          nextBase[p.id] = { r, c };
          if (firstRun) continue;
          const prev = base[p.id];
          if (!prev) continue; // new post of mine — seed silently
          if (r - prev.r > 0) {
            const dr = r - prev.r;
            newItems.push({
              id: `react-${p.id}-${Date.now()}`,
              type: "reaction",
              text: `${dr} new reaction${dr > 1 ? "s" : ""} on "${snippet(p.message || p.title)}"`,
              ts: new Date().toISOString(),
              read: false,
            });
          }
          if (c - prev.c > 0) {
            const dc = c - prev.c;
            let who = "";
            try {
              const cr = await api.get(`/community-v2/comments/${p.id}?post_type=${p.kind}`);
              const list = cr.data.comments || [];
              if (list.length && list[list.length - 1]?.anonymous_name) who = list[list.length - 1].anonymous_name;
            } catch { /* ignore */ }
            newItems.push({
              id: `comment-${p.id}-${Date.now()}`,
              type: "comment",
              text: who
                ? `${who} commented on "${snippet(p.message || p.title)}"`
                : `${dc} new comment${dc > 1 ? "s" : ""} on "${snippet(p.message || p.title)}"`,
              ts: new Date().toISOString(),
              read: false,
            });
          }
        }
        writeJSON(baseKey(uid), nextBase);
      } catch { /* ignore community errors */ }

      // ---- New direct messages ----
      try {
        const dmRes = await api.get("/dm/conversations");
        const convs = dmRes.data.conversations || [];
        const dmBase = readJSON(dmKey(uid), null);
        const dmFirst = dmBase === null;
        const nextDm = {};
        for (const c of convs) {
          nextDm[c.id] = c.last_message_at || "";
          if (dmFirst) continue;
          const prev = dmBase[c.id];
          const advanced = c.last_message_at && c.last_message_at !== prev && (!prev || c.last_message_at > prev);
          if (advanced && (Number(c.unread_count) || 0) > 0) {
            newItems.push({
              id: `dm-${c.id}-${Date.now()}`,
              type: "dm",
              text: `New message from ${c.other_user_name}: "${snippet(c.last_message)}"`,
              ts: new Date().toISOString(),
              read: false,
            });
          }
        }
        writeJSON(dmKey(uid), nextDm);
      } catch { /* ignore dm errors */ }

      if (newItems.length) {
        setNotifs((cur) => {
          const merged = [...newItems, ...cur].slice(0, MAX_NOTIFS);
          writeJSON(listKey(uid), merged);
          return merged;
        });
      }
    } finally {
      busy.current = false;
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [uid, poll]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (panelRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const unread = notifs.filter((n) => !n.read).length;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setNotifs((cur) => {
        const marked = cur.map((n) => ({ ...n, read: true }));
        if (uid) writeJSON(listKey(uid), marked);
        return marked;
      });
    }
  };

  const clearAll = () => {
    setNotifs([]);
    if (uid) writeJSON(listKey(uid), []);
  };

  const openItem = (n) => {
    setOpen(false);
    navigate(n.type === "dm" ? "/messages" : "/community");
  };

  if (!uid) return null;

  const iconFor = (type) =>
    type === "reaction" ? <Heart className="w-4 h-4" />
      : type === "dm" ? <MessageCircle className="w-4 h-4" />
        : <MessageSquare className="w-4 h-4" />;
  const tintFor = (type) =>
    type === "reaction" ? "bg-destructive/15 text-destructive"
      : type === "dm" ? "bg-seafoam/15 text-seafoam"
        : "bg-primary/15 text-primary";

  return (
    <div className="relative">
      <button ref={btnRef} data-testid="notification-bell" onClick={toggle}
        className="relative w-10 h-10 rounded-full flex items-center justify-center hover:bg-secondary transition-colors">
        <Bell className="w-5 h-5 text-foreground" />
        {unread > 0 && (
          <span data-testid="notification-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div ref={panelRef} data-testid="notification-panel"
          className="absolute right-0 mt-2 w-80 max-w-[90vw] bg-popover border border-border rounded-2xl shadow-xl z-50 overflow-hidden animate-fade-up">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <p className="font-heading font-semibold">Notifications</p>
            {notifs.length > 0 && (
              <button data-testid="notification-clear" onClick={clearAll}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <CheckCheck className="w-3.5 h-3.5" /> Clear
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-muted-foreground">
                <Bell className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No notifications yet.<br />We'll let you know about reactions, comments and messages.
              </div>
            ) : (
              notifs.map((n) => (
                <button key={n.id} data-testid="notification-item" onClick={() => openItem(n)}
                  className="w-full text-left flex gap-3 px-4 py-3 border-b border-border/60 last:border-0 hover:bg-secondary/40 transition-colors">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${tintFor(n.type)}`}>
                    {iconFor(n.type)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground/90 leading-snug">{n.text}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(n.ts)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
